import { create } from 'zustand';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  /** Auto-Dismiss-Timeout in ms. null = manuell schließen. Default 4000. */
  timeoutMs: number | null;
}

interface ToastInput {
  message: string;
  kind?: ToastKind;
  timeoutMs?: number | null;
}

interface ToastState {
  toasts: Toast[];
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

let nextId = 0;
function makeId(): string {
  nextId += 1;
  return `t_${Date.now().toString(36)}_${nextId}`;
}

const DEFAULT_TIMEOUT_MS = 4000;

/**
 * Globaler Toast-Store. Erste echte Zustand-Verwendung — bewusst klein
 * gehalten: nur push/dismiss, kein update-by-id (Toasts sind kurzlebig,
 * ein neuer Toast statt Update ist die saubere UX).
 *
 * Auto-Dismiss läuft im ToastContainer per setTimeout, nicht im Store —
 * Side-Effects gehören in die Component.
 */
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (input) => {
    const id = makeId();
    const toast: Toast = {
      id,
      kind: input.kind ?? 'info',
      message: input.message,
      timeoutMs: input.timeoutMs === undefined ? DEFAULT_TIMEOUT_MS : input.timeoutMs,
    };
    set((state) => ({ toasts: [...state.toasts, toast] }));
    return id;
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => set({ toasts: [] }),
}));

/**
 * Imperative Helfer für Mutation-Callbacks und andere Stellen, an denen
 * Hooks unhandlich wären. Äquivalent zu `useToastStore.getState().push(...)`,
 * mit den drei häufigsten Kinds als Shorthand.
 */
export const toast = {
  success: (message: string, timeoutMs?: number | null) =>
    useToastStore.getState().push({ message, kind: 'success', timeoutMs }),
  error: (message: string, timeoutMs?: number | null) =>
    useToastStore.getState().push({ message, kind: 'error', timeoutMs }),
  info: (message: string, timeoutMs?: number | null) =>
    useToastStore.getState().push({ message, kind: 'info', timeoutMs }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};
